// Keep in shape with RESOLUTION_QUERY_SHAPE in benchling/notebook/tables/api.py
// Keep in sync with StringResolutionType in resolve_strings_to_values.py
import * as PropTypes from 'prop-types';

import autopopulateAntibodyDomainsFromBioentitySummary from 'antibodies/registration-table/autopopulateAntibodyDomainsFromBioentitySummary';
import {NucleotideType} from 'biojs/BioConstants';
import {StorableItemType} from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {Batch, StorableSummary} from 'constants/SampleConstants';
import {EntitySummary} from 'constants/UserConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupSortType} from 'lookup-config/LookupConfigModalConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import * as ProcedureParameterCustomEvals from 'notebook/integrations/procedures/ProcedureParameterCustomEvals';
import {resolveTestDefinitionTableColumnValueFromSearchSummary} from 'notebook/integrations/procedures/ProcedureTestDefinitionCustomEvals';
import {validateExistingBioentityNotRegistered} from 'notebook/integrations/registry/CheckRegisteredStatusCustomEval';
import {loadExistingBioentityCustomEval} from 'notebook/integrations/registry/LegacyExistingBioentityCustomEval';
import {autopopulateFromBioentitySummary} from 'notebook/integrations/registry/RegistrationTableSchemaTagsFromExistingBioentityCustomEval';
import {SourceContainerResolver} from 'notebook/integrations/samples/ContainerSourceTypeCustomEval';
import {SourceEntityResolver} from 'notebook/integrations/samples/transfer-table-columns/BioentitySourceTypeCustomEval';
import {DestinationContainerResolver} from 'notebook/integrations/samples/transfer-table-columns/ContainerDestinationTypeCustomEval';
import * as DestinationMatrixPlateCustomEval from 'notebook/integrations/samples/transfer-table-columns/DestinationMatrixPlateCustomEval';
import {DestinationPlateResolver} from 'notebook/integrations/samples/transfer-table-columns/DestinationPlateCustomEval';
import {DestinationPositionResolver} from 'notebook/integrations/samples/transfer-table-columns/DestinationPositionCustomEval';
import {InventoryConcentrationResolver} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableConcentrationCustomEval';
import {
  InventoryQuantityResolver,
  TransferQuantityResolver,
} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableQuantityCustomEval';
import {ParentStorableResolver} from 'notebook/integrations/samples/transfer-table-columns/ParentStorableCustomEval';
import {SourcePlateResolver} from 'notebook/integrations/samples/transfer-table-columns/PlateSourceTypeCustomEval';
import {SampleControlRolesResolver} from 'notebook/integrations/samples/transfer-table-columns/SampleControlRolesCustomEval';
import {
  autopopulateContainerSchemaTags,
  autopopulatePlateWellTags,
} from 'notebook/integrations/samples/transfer-table-columns/SchemaTagsCustomEval';
import {
  AntibodyDomainColumnDefinition,
  ColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {coerceTypedValueToTypeQueryResolution} from 'notebook/tables/typed-values/DataTypes';
import validateParentStorable from 'notebook/tables/typed-values/validateParentStorable';
import {fetchGridAndValidateContainers} from 'notebook/transfer-tables/fetchGridAndValidateContainers';
import {validateGridCoordinates} from 'notebook/transfer-tables/validateGridCoordinates';
import {EquipmentTypedModel, EquipmentTypedValue} from 'procedures/ProcedureDomainTypes';
import {MaterialInputType} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {TestDefinitionsTableColumn} from 'procedures/tables/ProcedureTableConstants';
import {Position} from 'samples/SampleUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {autopopulateStudies} from 'studies/studyable/StudiesStructuredTableColumn';
import {Optional} from 'util/Types';

import {ResolutionQuery} from '../excess/Resolver';
import {PARTY_INFO_SHAPE, TypedValue} from '../TableConstants';
import {ResolutionQueryType} from '../TableStoreTransforms';
import {
  NumberTypedValue,
  PartyInfo,
  ProcedureConditionReplicatesTypedValue,
  ProcedureTypedValue,
  SampleRestrictionStatusTypedValue,
  StorablesTypedValue,
  StudiesTypedValue,
  UnitReferenceTypedValue,
} from '../typed-values/TypedValueTypes';

export enum StringResolutionType {
  BIOENTITY = 'BIOENTITY',
  BATCH = 'BATCH',
  DROPDOWN = 'DROPDOWN',
  STORABLE = 'STORABLE',
  ENTRY = 'ENTRY',
  USER = 'USER',
  CUSTOM = 'CUSTOM',
  PROCEDURE = 'PROCEDURE',
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',
  PROCESS_TASK = 'PROCESS_TASK',
  PARTY = 'PARTY',
  STUDY = 'STUDY',
  UNIT_REFERENCE = 'UNIT_REFERENCE',
}

interface StringResolutionQueryBioentityValueType {
  type: StringResolutionType.BIOENTITY;
  // Either tagSchemaId, folderItemType, or schemaInterfaceId is required
  tagSchemaId?: string;
  folderItemType?: string;
  schemaInterfaceId?: string;
}

interface StringResolutionQueryBatchValueType {
  type: StringResolutionType.BATCH;
  batchSchemaId: Optional<string>;
}

interface StringResolutionQueryDropdownValueType {
  type: StringResolutionType.DROPDOWN;
  schemaFieldId: string;
}

interface StringResolutionQueryEntryValueType {
  type: StringResolutionType.ENTRY;
}

interface StringResolutionQueryStorableValueType {
  type: StringResolutionType.STORABLE;
  storableType?: StorableItemType;
  storableSchemaId?: string;
}

interface StringResolutionQueryWorkflowTaskValueType {
  type: StringResolutionType.PROCESS_TASK;
}

interface StringResolutionQueryProcedureValueType {
  type: StringResolutionType.PROCEDURE;
}

interface StringResolutionQueryProcedureConditionReplicatesValueType {
  type: StringResolutionType.PROCEDURE_CONDITION_REPLICATES;
  fileId: string;
}

interface StringResolutionQueryStudyValueType {
  type: StringResolutionType.STUDY;
}

interface StringResolutionQueryUserValueType {
  type: StringResolutionType.USER;
}

interface StringResolutionQueryPartyValueType {
  type: StringResolutionType.PARTY;
}

interface StringResolutionQueryUnitValueType {
  type: StringResolutionType.UNIT_REFERENCE;
}

export type StringResolutionQueryValueType =
  | StringResolutionQueryBioentityValueType
  | StringResolutionQueryBatchValueType
  | StringResolutionQueryDropdownValueType
  | StringResolutionQueryEntryValueType
  | StringResolutionQueryStorableValueType
  | StringResolutionQueryUserValueType
  | StringResolutionQueryWorkflowTaskValueType
  | StringResolutionQueryPartyValueType
  | StringResolutionQueryProcedureValueType
  | StringResolutionQueryProcedureConditionReplicatesValueType
  | StringResolutionQueryStudyValueType
  | StringResolutionQueryUnitValueType;

// Keep in sync with RESOLUTION_QUERY_SHAPE in string_resolution.py
export interface StringResolutionQuery {
  string: string;
  valueType: StringResolutionQueryValueType;
  isMulti?: boolean;
  type: ResolutionQueryType.STRING;
}

// Each customEval function is likely to need a different query type.
// So each query type's name needs to be pretty specific, and quite long.
// That's okay.
export enum CustomResolutionQueryType {
  REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY = 'REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY',
  REGISTRATION_TABLE_CHECK_EXISTING_BIOENTITY_REGISTERED_STATUS = 'REGISTRATION_TABLE_CHECK_EXISTING_BIOENTITY_REGISTERED_STATUS',
  INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_OR_DESTINATION_CONTAINER = 'INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_OR_DESTINATION_CONTAINER',
  INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_GRID_POSITION_FROM_CONTAINER = 'INVENTORY_TABLE_GRID_POSITION_FROM_CONTAINER',
  INVENTORY_TABLE_PARENT_STORABLE_FROM_CONTAINER = 'INVENTORY_TABLE_PARENT_STORABLE_FROM_CONTAINER',
  INVENTORY_TABLE_PARENT_STORABLE_FROM_PLATE = 'INVENTORY_TABLE_PARENT_STORABLE_FROM_PLATE',
  INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_OR_DESTINATION_CONTAINER = 'INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_OR_DESTINATION_CONTAINER',
  INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_OR_DESTINATION_CONTAINER = 'INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_OR_DESTINATION_CONTAINER',
  INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_SCHEMA_TAGS_FROM_CONTAINER = 'INVENTORY_TABLE_SCHEMA_TAGS_FROM_CONTAINER',
  INVENTORY_TABLE_SCHEMA_TAGS_FROM_PLATE_WELL = 'INVENTORY_TABLE_SCHEMA_TAGS_FROM_PLATE_WELL',
  INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_CONTAINER = 'INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_CONTAINER',
  INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_PLATE',
  INVENTORY_VALIDATE_PARENT_STORABLE_CAPACITY = 'INVENTORY_VALIDATE_PARENT_STORABLE_CAPACITY',
  TRANSFER_TABLE_SOURCE_ENTITY_VALIDATE_IS_CONTAINABLE = 'TRANSFER_TABLE_SOURCE_ENTITY_VALIDATE_IS_CONTAINABLE',
  TRANSFER_TABLE_VALIDATE_DESTINATION_CONTAINER = 'TRANSFER_TABLE_VALIDATE_DESTINATION_CONTAINER',
  TRANSFER_TABLE_VALIDATE_DESTINATION_MATRIX_PLATE = 'TRANSFER_TABLE_VALIDATE_DESTINATION_MATRIX_PLATE',
  TRANSFER_TABLE_VALIDATE_DESTINATION_PLATE = 'TRANSFER_TABLE_VALIDATE_DESTINATION_PLATE',
  TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FILL_IN_PLATE = 'TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FILL_IN_PLATE',
  TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER = 'TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER',
  INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER = 'INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER',
  INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_VALIDATE_SOURCE_CONTAINER = 'INVENTORY_TABLE_VALIDATE_SOURCE_CONTAINER',
  INVENTORY_TABLE_VALIDATE_SOURCE_FIXED_PLATE = 'INVENTORY_TABLE_VALIDATE_SOURCE_FIXED_PLATE',
  INVENTORY_TABLE_VALIDATE_SOURCE_POSITION = 'INVENTORY_TABLE_VALIDATE_SOURCE_POSITION',
  INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER = 'INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER',
  INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER = 'INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER',
  INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE = 'INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE',
  INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_CONTAINER = 'INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_CONTAINER',
  INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_CONTAINER = 'INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_CONTAINER',
  INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_PLATE = 'INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_PLATE',
  INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_PLATE = 'INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_PLATE',
  TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER = 'TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER',
  TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE = 'TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE',
  STRUCTURED_TABLE_COERCE_TYPED_VALUE_TO_TYPE = 'STRUCTURED_TABLE_COERCE_TYPED_VALUE_TO_TYPE',
  LOOKUP_COLUMN = 'LOOKUP_COLUMN',
  VALIDATE_PARENT_STORABLE = 'VALIDATE_PARENT_STORABLE',
  SNAPSHOT_FIELD_PREVIEW = 'SNAPSHOT_FIELD_PREVIEW',
  REGISTRATION_TABLE_VALIDATE_SMILES = 'REGISTRATION_TABLE_VALIDATE_SMILES_BASES',
  STOICHIOMETRY_TABLE_GET_MOLECULAR_WEIGHT = 'STOICHIOMETRY_TABLE_GET_MOLECULAR_WEIGHT',
  STOICHIOMETRY_TABLE_GET_MOLECULAR_FORMULA = 'STOICHIOMETRY_TABLE_GET_MOLECULAR_FORMULA',
  LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES = 'LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES',
  LEGACY_TRANSFER_TABLE_VALIDATED_DESTINATION_CONTAINERS = 'LEGACY_TRANSFER_TABLE_VALIDATED_DESTINATION_CONTAINERS',
  REGISTRATION_TABLE_VALIDATE_HELM = 'REGISTRATION_TABLE_VALIDATE_HELM',
  REGISTRATION_TABLE_VALIDATE_IDT = 'REGISTRATION_TABLE_VALIDATE_IDT',
  REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX = 'REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX',
  STUDIES_COLUMN = 'STUDIES_COLUMN',
  PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION = 'PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION',
  PROCEDURES_VALIDATE_MATERIAL_INPUT_CONFIRMATION_METHOD_AND_DATA_TYPE = 'PROCEDURES_VALIDATE_MATERIAL_INPUT_CONFIRMATION_METHOD_AND_DATA_TYPE',
  TEST_DEFINITION_SUMMARY = 'TEST_DEFINITION_SUMMARY',
  REGISTRATION_TABLE_ANTIBODY_DOMAINS_FROM_EXISTING_BIOENTITY = 'REGISTRATION_TABLE_ANTIBODY_DOMAINS_FROM_EXISTING_BIOENTITY',
  UNITS = 'UNITS',
  LEGACY_REGISTRATION_TABLE_EXISTING_BIOENTITY = 'LEGACY_REGISTRATION_TABLE_EXISTING_BIOENTITY',
}

/**
 * Type definition for queries that should go through "custom resolution".
 *
 * Custom resolution queries share some infrastructure with string resolution.
 * Specifically, they share logic for batching queries and caching query results.
 *
 * However, custom resolution queries are free to define their own business logic.
 * This business logic can be very different from string resolution:
 * - Queries can be arbitrary JSON, not just a string.
 * - Queries can resolved using any backend data source, not just the string resolution endpoint.
 * - Queries can return any typed value
 */
export type CustomResolutionQuery =
  | StudiesColumnQuery
  | InventoryTableCustomResolutionQuery
  | RegistrationTableSchemaTagsFromExistingBioentityCustomEvalQuery
  | RegistrationTableCheckExistingBioentityRegisteredStatusCustomEvalQuery
  | TransferTableSourceEntityValidateIsContainableQuery
  | TransferTableValidateDestinationContainerQuery
  | TransferTableValidateDestinationMatrixPlateQuery
  | TransferTableValidateDestinationPlateQuery
  | TransferTableValidateDestinationPositionFillInPlateQuery
  | TransferTableValidateDestinationPositionForCreateContainerQuery
  | TransferTableValidateQuantityWithSourceContainerQuery
  | TransferTableValidateQuantityWithSourcePlateQuery
  | StructuredTableCoerceTypedValueToTypeQuery
  | LookupColumnQuery
  | SnapshotFieldPreviewQuery
  | ValidateParentStorableQuery
  | ValidateSmilesQuery
  | GetMolecularWeightQuery
  | GetMolecularFormulaQuery
  | LegacyTransferTableValidateGridCoordinatesQuery
  | LegacyTransferTableValidatedDestinationContainersQuery
  | ValidateHelmQuery
  | ValidateIDTQuery
  | ValidateCustomSyntaxQuery
  | ProceduresEquipmentConfirmationValidationQuery
  | ProceduresValidateMaterialInputConfirmationMethodAndDataTypeQuery
  | TestDefinitionTableColumnValueQuery
  | RegistrationTableAntibodyDomainsFromExistingBioentityCustomEvalQuery
  | UnitsCustomResolutionQuery
  | LegacyRegistrationTableLoadExistingBioentityCustomEvalQuery;

type InventoryTableCustomResolutionQuery =
  | InventoryTableRestrictedSampleUsersFromSourceOrDestinationContainerQuery
  | InventoryTableRestrictedSampleUsersFromSourcePlateQuery
  | InventoryTableGridPositionFromContainerQuery
  | InventoryTableParentStorableFromContainerQuery
  | InventoryTableParentStorableFromPlateQuery
  | InventoryTableQuantityAmountFromSourceContainerQuery
  | InventoryTableQuantityAmountFromSourcePlateQuery
  | InventoryTableQuantityUnitsFromSourceContainerQuery
  | InventoryTableQuantityUnitsFromSourcePlateQuery
  | InventoryTableConcentrationAmountFromContainerQuery
  | InventoryTableConcentrationUnitsFromContainerQuery
  | InventoryTableConcentrationAmountFromPlateQuery
  | InventoryTableConcentrationUnitsFromPlateQuery
  | InventoryTableRestrictionStatusFromSourceOrDestinationContainerQuery
  | InventoryTableRestrictionStatusFromSourcePlateQuery
  | InventoryTableSampleOwnersFromSourceOrDestinationContainerQuery
  | InventoryTableSampleOwnersFromSourcePlateQuery
  | InventoryTableSchemaTagsFromContainerQuery
  | InventoryTableSchemaTagsFromPlateWellQuery
  | InventoryTableSourceEntityFromSourceContainerQuery
  | InventoryTableSourceEntityFromSourcePlateQuery
  | InventoryTableValidateAddDeductFromSourceContainerQuery
  | InventoryTableValidateAddDeductFromSourcePlateQuery
  | InventoryTableValidateSourceContainerQuery
  | InventoryTableValidateSourceFixedPlateQuery
  | InventoryTableValidateSourcePositionQuery
  | InventoryValidateParentStorableCapacityQuery;

interface BaseCustomResolutionQuery {
  type: ResolutionQueryType.CUSTOM;
}

export interface StudiesColumnQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.STUDIES_COLUMN;
  studyableId: string;
}

export interface RegistrationTableSchemaTagsFromExistingBioentityCustomEvalQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY;
  bioentityId: string;
  columnDefinition: ColumnDefinition;
  typedValue: TypedValue;
}

export interface RegistrationTableCheckExistingBioentityRegisteredStatusCustomEvalQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_CHECK_EXISTING_BIOENTITY_REGISTERED_STATUS;
  bioentityId: string;
  columnDefinition: ColumnDefinition;
  typedValue: TypedValue;
}

export interface LegacyRegistrationTableLoadExistingBioentityCustomEvalQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.LEGACY_REGISTRATION_TABLE_EXISTING_BIOENTITY;
  bioentityId: string;
  columnDefinition: ColumnDefinition;
  tagSchema: TagSchema;
}

export interface InventoryTableGridPositionFromContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_GRID_POSITION_FROM_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableParentStorableFromContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableParentStorableFromPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_PLATE;
  plateId: string;
  plateSchemaId?: string | null;
}
interface BaseInventoryTableRestrictedSampleUsersQuery extends BaseCustomResolutionQuery {
  destinationContainerId: string | null;
  destinationSchemaId: string | null;
  isSpecifyingSampleOwners: boolean | null;
  isSpecifyingRestrictedSampleUsers: boolean | null;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}
interface BaseInventoryTableSampleOwnersQuery extends BaseCustomResolutionQuery {
  destinationContainerId: string | null;
  destinationSchemaId: string | null;
  isSpecifyingSampleOwners: boolean | null;
}
interface BaseInventoryTableRestrictionStatusQuery extends BaseCustomResolutionQuery {
  destinationContainerId: string | null;
  destinationSchemaId: string | null;
  specifiedRestrictionStatus: SampleRestrictionStatusTypedValue['status'] | null;
}
export interface InventoryTableRestrictedSampleUsersFromSourceOrDestinationContainerQuery extends BaseInventoryTableRestrictedSampleUsersQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_OR_DESTINATION_CONTAINER;
  sourceContainerId: string | null;
}
export interface InventoryTableRestrictedSampleUsersFromSourcePlateQuery extends BaseInventoryTableRestrictedSampleUsersQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_PLATE;
  sourcePlateId: string;
  sourcePosition: string;
}
export interface InventoryTableRestrictionStatusFromSourceOrDestinationContainerQuery extends BaseInventoryTableRestrictionStatusQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_OR_DESTINATION_CONTAINER;
  sourceContainerId: string | null;
}
export interface InventoryTableRestrictionStatusFromSourcePlateQuery extends BaseInventoryTableRestrictionStatusQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_PLATE;
  sourcePlateId: string;
  sourcePosition: string;
}
export interface InventoryTableSampleOwnersFromSourceOrDestinationContainerQuery extends BaseInventoryTableSampleOwnersQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_OR_DESTINATION_CONTAINER;
  sourceContainerId: string | null;
}
export interface InventoryTableSampleOwnersFromSourcePlateQuery extends BaseInventoryTableSampleOwnersQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_PLATE;
  sourcePlateId: string;
  sourcePosition: string;
}
export interface InventoryTableQuantityAmountFromSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableQuantityAmountFromSourcePlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE;
  plateId: string;
  position: string;
  plateSchemaId?: string | null;
}
export interface InventoryTableQuantityUnitsFromSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableQuantityUnitsFromSourcePlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE;
  plateId: string;
  position: string;
  plateSchemaId?: string | null;
}
export interface InventoryTableConcentrationAmountFromContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableConcentrationUnitsFromContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
}
export interface InventoryTableConcentrationAmountFromPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_PLATE;
  plateId: string;
  position: string;
  plateSchemaId?: string | null;
}
export interface InventoryTableConcentrationUnitsFromPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_PLATE;
  plateId: string;
  position: string;
  plateSchemaId?: string | null;
}
export interface InventoryTableSchemaTagsFromContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_CONTAINER;
  containerId: string;
  tagSchemaFieldApiId: string;
}
export interface InventoryTableSchemaTagsFromPlateWellQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_PLATE_WELL;
  plateId: string;
  positionString: string;
  plateSchemaId?: string | null;
  tagSchemaFieldApiId: string;
}

export interface InventoryTableSourceEntityFromSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_CONTAINER;
  containerId: string;
  containerSchemaId?: string | null;
  allowMultiContentsContainer?: boolean;
}
export interface InventoryTableSourceEntityFromSourcePlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_PLATE;
  plateId: string;
  position: string;
  allowMultiContentsWell?: boolean;
  plateSchemaId?: string | null;
  plateSchemaType?: PlateSchemaType;
}
export interface TransferTableSourceEntityValidateIsContainableQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_SOURCE_ENTITY_VALIDATE_IS_CONTAINABLE;
  bioentityId: string;
}

export interface TransferTableValidateDestinationContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_CONTAINER;
  containerId: string;
  containerSchemaId: string | null;
  containerSchemaName: string | null;
}

export interface TransferTableValidateDestinationMatrixPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_MATRIX_PLATE;
  plateId: string;
}

export interface TransferTableValidateDestinationPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_PLATE;
  plateId: string;
  plateSchemaId: string;
}

export interface TransferTableValidateDestinationPositionFillInPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FILL_IN_PLATE;
  plateId: string;
  position: string;
}

export interface TransferTableValidateDestinationPositionForCreateContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER;
  gridType: 'BOX' | 'MATRIX_PLATE';
  boxOrPlateId: string;
  position: string;
}

export interface InventoryTableValidateAddDeductFromSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER;
  containerId: string;
  addDeductValue: number;
}

export interface InventoryTableValidateAddDeductFromSourcePlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE;
  plateId: string;
  position: string;
  addDeductValue: number;
}

export interface InventoryTableValidateSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_CONTAINER;
  containerId: string;
  allowEmptyContainer?: boolean;
  allowMultiContentsContainer?: boolean;
  containerSchemaId?: string | null;
  containerSchemaName?: string | null;
}

export interface InventoryTableValidateSourceFixedPlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_FIXED_PLATE;
  plateId: string;
  plateSchemaId: string | null;
  plateSchemaName: string | null;
}

export interface InventoryTableValidateSourcePositionQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_POSITION;
  plateId: string;
  position: string;
  allowEmptyWell?: boolean;
  allowMultiContentsWell?: boolean;
}

export interface InventoryValidateParentStorableCapacityQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.INVENTORY_VALIDATE_PARENT_STORABLE_CAPACITY;
  parentStorableId: string;
  childSchemaId: string;
  childSchemaName: string;
}

export interface TransferTableValidateQuantityWithSourceContainerQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER;
  containerId: string;
  quantityValue: number;
  quantityUnits: string;
}

export interface TransferTableValidateQuantityWithSourcePlateQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE;
  plateId: string;
  position: string;
  quantityValue: number;
  quantityUnits: string;
}

export interface ValidateParentStorableQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.VALIDATE_PARENT_STORABLE;
  storableId: string;
  isForTransfer: boolean;
  expectedGridContainerSchemaId: string | null;
}

export interface StructuredTableCoerceTypedValueToTypeQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.STRUCTURED_TABLE_COERCE_TYPED_VALUE_TO_TYPE;
  typedValue: TypedValue;
  fieldType: FieldType;
  languageTag: string | null;
  timezoneName: string | null;
}

export interface LookupColumnQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.LOOKUP_COLUMN;
  inputValueId: string;
  lookupConfigId: string;
  lookupSortType?: LookupSortType | null;
}

export interface SnapshotFieldPreviewQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.SNAPSHOT_FIELD_PREVIEW;
  inputValueIds: Array<string>;
  gridPositionInputValue?: string | null;
  snapshotFieldId: string;
  fileId: string | null;
}

export interface ValidateSmilesQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_SMILES;
  smilesString: string;
}

export interface GetMolecularWeightQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULAR_WEIGHT;
  moleculeId: string;
}

export interface GetMolecularFormulaQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULAR_FORMULA;
  moleculeId: string;
}

export interface LegacyTransferTableValidateGridCoordinatesQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES;
  positionTypedValue: TypedValue;
  parentStorableTypedValue: StorablesTypedValue;
}

export interface LegacyTransferTableValidatedDestinationContainersQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATED_DESTINATION_CONTAINERS;
  containersTypedValue: TypedValue;
  parentStorableTypedValue: StorablesTypedValue;
  numContainersTypedValue: NumberTypedValue;
  sourceContainerTypedValue: TypedValue | null;
  positions: Array<Position>;
}

export interface ValidateHelmQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_HELM;
  nucleotideType: NucleotideType;
  helmString: string;
}

export interface ValidateIDTQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_IDT;
  nucleotideType: NucleotideType;
  idtString: string;
}

export interface ValidateCustomSyntaxQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX;
  nucleotideType: NucleotideType;
  customSyntaxString: string;
  customSyntaxName: string;
}

export interface ProceduresEquipmentConfirmationValidationQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION;
  bioentityId: string;
  equipmentTypedValue: EquipmentTypedValue;
  modelTypedValue: EquipmentTypedModel;
}

export interface ProceduresValidateMaterialInputConfirmationMethodAndDataTypeQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.PROCEDURES_VALIDATE_MATERIAL_INPUT_CONFIRMATION_METHOD_AND_DATA_TYPE;
  confirmationMethod: ProcedureParameterConfirmationMethod;
  dataType: MaterialInputType;
}

export interface TestDefinitionTableColumnValueQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.TEST_DEFINITION_SUMMARY;
  testDefinitionId: string;
  column: TestDefinitionsTableColumn;
}

export interface RegistrationTableAntibodyDomainsFromExistingBioentityCustomEvalQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_ANTIBODY_DOMAINS_FROM_EXISTING_BIOENTITY;
  bioentityId: string;
  columnDefinition: AntibodyDomainColumnDefinition;
}

export interface UnitsCustomResolutionQuery extends BaseCustomResolutionQuery {
  customResolutionQueryType: CustomResolutionQueryType.UNITS;
  unitIds: Array<string>;
}

export function isCustomResolutionQuery(query: ResolutionQuery): query is CustomResolutionQuery {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'customResolutionQueryType' in query;
}

export const BIOENTITY_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.BIOENTITY]).isRequired,
  bioentityId: PropTypes.string.isRequired,
});
export const BATCH_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.BATCH]).isRequired,
  batchId: PropTypes.string.isRequired,
});
export const DROPDOWN_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.DROPDOWN]).isRequired,
  schemaFieldSelectorOptionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
});
export const STORABLE_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.STORABLE]).isRequired,
  storableId: PropTypes.string.isRequired,
});
export const USER_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.USER]).isRequired,
  userHandles: PropTypes.arrayOf(PropTypes.string).isRequired,
});
export const WORKFLOW_TASK_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.PROCESS_TASK]).isRequired,
  id: PropTypes.string.isRequired,
  humanId: PropTypes.string.isRequired,
});
export const PARTY_RESOLVED_VALUE_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf([StringResolutionType.PARTY]).isRequired,
  partySummaries: PropTypes.arrayOf(PARTY_INFO_SHAPE.isRequired).isRequired,
});

export interface BioentityResolvedValue {
  bioentityIds: Array<string>;
}

export interface BatchResolvedValue {
  batchIds: Array<string>;
}

export interface DropdownResolvedValue {
  schemaFieldSelectorOptionIds: Array<string>;
  value: string;
}

export interface EntryResolvedValue {
  entryIds: Array<string>;
}

export interface StorableResolvedValue {
  storableIds: Array<string>;
}

export interface UserResolvedValue {
  userHandles: Array<string>;
}

export interface ProcessTaskResolvedValue {
  id: string;
  humanId: string;
}

export interface PartyResolvedValue {
  partySummaries: Array<PartyInfo>;
}

/**
 * Custom resolution queries can resolve to any typed value.
 * If no return value is needed, a query can also return `null`.
 */
export interface CustomResolvedValue {
  typedValue: TypedValue | null;
}

export type StringResolvedValue =
  | BioentityResolvedValue
  | BatchResolvedValue
  | DropdownResolvedValue
  | EntryResolvedValue
  | PartyResolvedValue
  | ProcessTaskResolvedValue
  | StorableResolvedValue
  | UserResolvedValue
  | CustomResolvedValue
  | ProcedureTypedValue
  | ProcedureConditionReplicatesTypedValue
  | StudiesTypedValue
  | UnitReferenceTypedValue;

/**
 * The result of a resolved query, or the error message if it errored.
 *
 * Both the resolvedValue and the error will be persisted.
 * It's okay to change error messages, but changes won't be user-visible
 * until the query gets re-resolved.
 */
export interface StringResolutionResult {
  error: Optional<string>;
  isWarning?: boolean;
  resolvedValue: Optional<StringResolvedValue>;
}

export const STRING_RESOLUTION_RESULT_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    error: PropTypes.string.isRequired,
    isWarning: PropTypes.string,
  }),
  PropTypes.shape({
    resolvedValue: PropTypes.oneOfType([
      BIOENTITY_RESOLVED_VALUE_SHAPE,
      BATCH_RESOLVED_VALUE_SHAPE,
      DROPDOWN_RESOLVED_VALUE_SHAPE,
      PARTY_RESOLVED_VALUE_SHAPE,
      STORABLE_RESOLVED_VALUE_SHAPE,
      USER_RESOLVED_VALUE_SHAPE,
      WORKFLOW_TASK_RESOLVED_VALUE_SHAPE,
    ]).isRequired,
  }),
]);

interface StringResolutionErrorResult {
  type: undefined;
  error: string;
  isWarning?: boolean;
  resolvedValue: undefined;
}

interface BioentityServerResolutionResult {
  type: StringResolutionType.BIOENTITY;
  error: undefined;
  resolvedValue: BioentityResolvedValue;
  bioentitySummaries: Array<BioentitySummary>;
}

export interface BatchServerResolutionResult {
  type: StringResolutionType.BATCH;
  error: undefined;
  resolvedValue: BatchResolvedValue;
  batches: Array<Batch>;
}

interface DropdownServerResolutionResult {
  type: StringResolutionType.DROPDOWN;
  error: undefined;
  resolvedValue: DropdownResolvedValue;
}

interface EntryServerResolutionResult {
  type: StringResolutionType.ENTRY;
  error: undefined;
  resolvedValue: EntryResolvedValue;
  entrySummaries: Array<EntrySummary>;
}

interface PartyServerResolutionResult {
  type: StringResolutionType.PARTY;
  error: undefined;
  resolvedValue: PartyResolvedValue;
}

interface ProcedureServerResolutionResult {
  type: StringResolutionType.PROCEDURE;
  error: undefined;
  resolvedValue: ProcedureTypedValue;
}

interface ProcedureConditionReplicatesResolutionResult {
  type: StringResolutionType.PROCEDURE_CONDITION_REPLICATES;
  error: undefined;
  resolvedValue: ProcedureTypedValue;
}

interface StudyServerResolutionResult {
  type: StringResolutionType.STUDY;
  error: undefined;
  resolvedValue: StudiesTypedValue;
}

interface StorableServerResolutionResult {
  type: StringResolutionType.STORABLE;
  error: undefined;
  resolvedValue: StorableResolvedValue;
  storableSummaries: Array<StorableSummary>;
}

interface UnitReferenceServerResolutionResult {
  type: StringResolutionType.UNIT_REFERENCE;
  error: undefined;
  resolvedValue: UnitReferenceTypedValue;
}

interface UserServerResolutionResult {
  type: StringResolutionType.USER;
  error: undefined;
  resolvedValue: UserResolvedValue;
  entitySummaries: Array<EntitySummary>;
}

interface WorkflowTaskServerResolutionResult {
  type: StringResolutionType.PROCESS_TASK;
  error: undefined;
  resolvedValue: ProcessTaskResolvedValue;
}

/**
 * Custom resolution queries can resolve to any typed value.
 * If no return value is needed, a query can also return `null`.
 *
 * Custom resolution queries should NOT return any other type,
 * such as a full Container object.
 *
 * By keeping resolution results limited to typed values, we minimize the surface area
 * that related features have to deal with. For example, if we want cells
 * with out-of-date resolution queries to show stale indicators,
 * we need to fetch live resolution results and compare them to what's persisted.
 * If resolution results could be any type, this comparison would be difficult to reason about.
 */
export interface CustomServerResolutionResult {
  type: StringResolutionType.CUSTOM;
  error: Optional<string>;
  isWarning?: boolean;
  resolvedValue: {typedValue: TypedValue | null};
}

export function isCustomServerResolutionResult(
  serverResolutionResult: ServerResolutionResult
): serverResolutionResult is CustomServerResolutionResult {
  return serverResolutionResult.type === StringResolutionType.CUSTOM;
}

export type ServerResolutionResult =
  | StringResolutionErrorResult
  | BioentityServerResolutionResult
  | BatchServerResolutionResult
  | DropdownServerResolutionResult
  | EntryServerResolutionResult
  | PartyServerResolutionResult
  | StorableServerResolutionResult
  | UserServerResolutionResult
  | WorkflowTaskServerResolutionResult
  | CustomServerResolutionResult
  | ProcedureServerResolutionResult
  | ProcedureConditionReplicatesResolutionResult
  | StudyServerResolutionResult
  | UnitReferenceServerResolutionResult;

const InventoryTableCustomResolutionQueryTypesToResolutionFn: {
  [queryType: string]: (query: CustomResolutionQuery) => Promise<ServerResolutionResult>;
} = {
  [CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_OR_DESTINATION_CONTAINER]:
    SampleControlRolesResolver.validateOrAutopopulateRestrictedSampleUsersFromSourceOrDestinationContainer,
  [CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTED_SAMPLE_USERS_FROM_SOURCE_PLATE]:
    SampleControlRolesResolver.validateOrAutopopulateRestrictedSampleUsersFromSourcePlate,
  [CustomResolutionQueryType.INVENTORY_TABLE_GRID_POSITION_FROM_CONTAINER]:
    ParentStorableResolver.autopopulateContainerGridPosition,
  [CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_CONTAINER]:
    ParentStorableResolver.autopopulateContainerParentStorable,
  [CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_PLATE]:
    ParentStorableResolver.autopopulatePlateParentStorable,
  [CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER]:
    InventoryQuantityResolver.autopopulateContainerQuantityAmount,
  [CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE]:
    InventoryQuantityResolver.autopopulatePlateQuantityAmount,
  [CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_CONTAINER]:
    InventoryQuantityResolver.autopopulateContainerQuantityUnits,
  [CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_UNITS_FROM_SOURCE_PLATE]:
    InventoryQuantityResolver.autopopulatePlateQuantityUnits,
  [CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_CONTAINER]:
    InventoryConcentrationResolver.autopopulateContainerConcentrationAmount,
  [CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_CONTAINER]:
    InventoryConcentrationResolver.autopopulateContainerConcentrationUnits,
  [CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_PLATE]:
    InventoryConcentrationResolver.autopopulatePlateConcentrationAmount,
  [CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_PLATE]:
    InventoryConcentrationResolver.autopopulatePlateConcentrationUnits,
  [CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_OR_DESTINATION_CONTAINER]:
    SampleControlRolesResolver.validateOrAutopopulateRestrictionStatusFromSourceOrDestinationContainer,
  [CustomResolutionQueryType.INVENTORY_TABLE_RESTRICTION_STATUS_FROM_SOURCE_PLATE]:
    SampleControlRolesResolver.validateOrAutopopulateRestrictionStatusFromSourcePlate,
  [CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_OR_DESTINATION_CONTAINER]:
    SampleControlRolesResolver.validateOrAutopopulateSampleOwnersFromSourceOrDestinationContainer,
  [CustomResolutionQueryType.INVENTORY_TABLE_SAMPLE_OWNERS_FROM_SOURCE_PLATE]:
    SampleControlRolesResolver.validateOrAutopopulateSampleOwnersFromSourcePlate,
  [CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_CONTAINER]: autopopulateContainerSchemaTags,
  [CustomResolutionQueryType.INVENTORY_TABLE_SCHEMA_TAGS_FROM_PLATE_WELL]: autopopulatePlateWellTags,
  [CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_CONTAINER]:
    SourceContainerResolver.autopopulateSourceEntity,
  [CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_PLATE]:
    SourcePlateResolver.autopopulateSourceEntity,
  [CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_CONTAINER]:
    InventoryQuantityResolver.validateAddDeductFromSourceContainer,
  [CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_ADD_DEDUCT_FROM_SOURCE_PLATE]:
    InventoryQuantityResolver.validateAddDeductFromSourcePlate,
  [CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_CONTAINER]:
    SourceContainerResolver.validateSourceContainer,
  [CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_FIXED_PLATE]:
    SourcePlateResolver.validateSourceFixedPlate,
  [CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_POSITION]:
    SourcePlateResolver.validateSourcePosition,
  [CustomResolutionQueryType.INVENTORY_VALIDATE_PARENT_STORABLE_CAPACITY]:
    ParentStorableResolver.validateParentStorableCapacity,
};

/*
  This Map maps each custom resolution query type to the async function resolving the
  query to a ServerResolutionResult. If you are adding a new custom resolution that uses GraphQL
  or otherwise does it's own batching, just add your function here. If you need to add your own batching,
  follow the example of CustomResolutionQueryType.LOOKUP_COLUMN.
 */
export const CustomResolutionQueryTypesToResolutionFn: {
  [queryType: string]: (query: CustomResolutionQuery) => Promise<ServerResolutionResult>;
} = {
  ...InventoryTableCustomResolutionQueryTypesToResolutionFn,
  [CustomResolutionQueryType.REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY]:
    autopopulateFromBioentitySummary,
  [CustomResolutionQueryType.REGISTRATION_TABLE_CHECK_EXISTING_BIOENTITY_REGISTERED_STATUS]:
    validateExistingBioentityNotRegistered,
  [CustomResolutionQueryType.TRANSFER_TABLE_SOURCE_ENTITY_VALIDATE_IS_CONTAINABLE]:
    SourceEntityResolver.validateSourceEntityIsContainable,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_CONTAINER]:
    DestinationContainerResolver.validateDestinationContainer,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_MATRIX_PLATE]:
    DestinationMatrixPlateCustomEval.validateDestinationMatrixPlate,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FILL_IN_PLATE]:
    DestinationPositionResolver.validateDestinationPlatePosition,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_PLATE]:
    DestinationPlateResolver.validateDestinationPlate,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_POSITION_FOR_CREATE_CONTAINER]:
    DestinationPositionResolver.validateDestinationPositionForBoxOrMatrixPlate,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER]:
    TransferQuantityResolver.validateQuantityWithSourceContainer,
  [CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE]:
    TransferQuantityResolver.validateQuantityWithSourcePlate,
  [CustomResolutionQueryType.STRUCTURED_TABLE_COERCE_TYPED_VALUE_TO_TYPE]:
    coerceTypedValueToTypeQueryResolution,
  [CustomResolutionQueryType.VALIDATE_PARENT_STORABLE]: validateParentStorable,
  [CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES]: validateGridCoordinates,
  [CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATED_DESTINATION_CONTAINERS]:
    fetchGridAndValidateContainers,
  // TODO: Consider making CustomResolutionQueryTypesToResolutionFn private, so that a getter
  // can be expose that enumerate StructuredColumnAdapters, which can then advertise a
  // custom resolution query.
  [CustomResolutionQueryType.STUDIES_COLUMN]: autopopulateStudies,
  [CustomResolutionQueryType.PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION]:
    ProcedureParameterCustomEvals.validateEquipmentConfirmation,
  [CustomResolutionQueryType.PROCEDURES_VALIDATE_MATERIAL_INPUT_CONFIRMATION_METHOD_AND_DATA_TYPE]:
    ProcedureParameterCustomEvals.validateMaterialInputConfirmationMethodAndDataType,
  [CustomResolutionQueryType.TEST_DEFINITION_SUMMARY]: resolveTestDefinitionTableColumnValueFromSearchSummary,
  [CustomResolutionQueryType.REGISTRATION_TABLE_ANTIBODY_DOMAINS_FROM_EXISTING_BIOENTITY]:
    autopopulateAntibodyDomainsFromBioentitySummary,
  [CustomResolutionQueryType.LEGACY_REGISTRATION_TABLE_EXISTING_BIOENTITY]: loadExistingBioentityCustomEval,
};

export function isLookupColumnQuery(query: ResolutionQuery): query is LookupColumnQuery {
  return (
    query.type === ResolutionQueryType.CUSTOM &&
    query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN
  );
}
